import React, { useState } from "react";
import { ArrowRight, Phone, Play, ShieldCheck, BadgeCheck, Clock, Building2 } from "lucide-react";
import { trackPhoneClick, trackEvent } from "../utils/analytics";
import VideoModal from "./VideoModal";

const COMMAND_WALL =
  "https://static.prod-images.emergentagent.com/jobs/d6d19dde-3ac3-43df-a169-28a9d2505ed7/images/02b2bfbd575855a7fba64d15d72a1319c17445cf618b7bf2722320f5a390e3cc.png";

const TRUST = [
  { icon: BadgeCheck, label: "Licensed", sub: "TX DPS #B30959201" },
  { icon: ShieldCheck, label: "Fully insured", sub: "Professional liability" },
  { icon: Clock, label: "24/7 live", sub: "Real-time monitoring" },
  { icon: Building2, label: "Luxury & multi-family", sub: "Built for communities" },
];

const Hero = () => {
  const [showVideo, setShowVideo] = useState(false);
  const goConsult = () => document.getElementById("consultation")?.scrollIntoView({ behavior: "smooth" });
  const openVideo = () => {
    trackEvent("video_open", { category: "video_engagement", action: "hero_play_click", label: "virtual_guards_demo", value: 30 });
    setShowVideo(true);
  };

  return (
    <section id="home" className="relative bg-white pt-32 lg:pt-40 pb-20 lg:pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Message */}
          <div>
            <div data-reveal className="inline-flex items-center gap-2 mb-6 rounded-full bg-[#D32F2F]/8 border border-[#D32F2F]/15 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#D32F2F]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D32F2F] pulse-dot" />
              For Luxury Communities, Condos &amp; Multi-Family Residences
            </div>

            <h1 data-reveal data-reveal-delay="1"
                className="font-head text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold tracking-tight leading-[1.05] text-[#0F172A] mb-6">
              Premier video security monitoring for <span className="text-[#D32F2F]">premium communities.</span>
            </h1>

            <p data-reveal data-reveal-delay="2" className="text-lg text-[#0F172A]/60 max-w-xl leading-relaxed mb-8">
              Real protection for premium properties that expect more than cameras. A dedicated virtual remote
              guard — backed by a real monitoring operation — watches your community in real time and engages
              the moment something looks wrong, instead of just recording it after the fact.
            </p>

            <div data-reveal data-reveal-delay="3" className="flex flex-wrap items-center gap-4 mb-10">
              <button onClick={goConsult} data-testid="hero-consultation-btn"
                      className="group inline-flex items-center gap-3 rounded-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white text-sm font-medium px-8 py-4 transition-all hover:scale-[1.02]">
                Request a Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="tel:+15123636584" onClick={() => trackPhoneClick("hero")}
                 className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/12 px-7 py-4 text-sm font-semibold text-[#0F172A] hover:border-[#D32F2F]/40 hover:text-[#D32F2F] transition-colors">
                <Phone className="w-4 h-4 text-[#D32F2F]" />(512) 363-6584
              </a>
            </div>

            {/* Trust strip */}
            <div data-reveal data-reveal-delay="4" className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5 pt-8 border-t border-[#0F172A]/10">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-start gap-2.5">
                  <t.icon className="w-5 h-5 text-[#D32F2F] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[13px] font-semibold text-[#0F172A] leading-tight">{t.label}</div>
                    <div className="text-[11px] text-[#0F172A]/45 mt-0.5">{t.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monitoring visual */}
          <div data-reveal data-reveal-delay="2" className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#0F172A]/10 shadow-[0_30px_80px_-24px_rgba(15,23,42,0.45)]">
              <img src={COMMAND_WALL} alt="Redline command center monitoring luxury condominium camera feeds in real time"
                   className="w-full h-[420px] lg:h-[560px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md border border-white/15 px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#FF3B3B] pulse-dot" />
                <span className="text-[11px] uppercase tracking-[0.16em] text-white font-semibold">Live · Command Center</span>
              </div>
              <button onClick={openVideo} data-testid="hero-video-play"
                      className="absolute inset-0 flex items-center justify-center group" aria-label="Play brand video">
                <span className="backdrop-blur-md bg-white/20 border border-white/30 rounded-full p-6 group-hover:bg-[#D32F2F] group-hover:scale-110 transition-all duration-300">
                  <Play className="w-7 h-7 text-white fill-white ml-1" />
                </span>
              </button>
            </div>
            <div className="absolute -bottom-5 left-6 bg-white rounded-xl shadow-[0_18px_40px_-12px_rgba(15,23,42,0.35)] border border-[#0F172A]/8 px-5 py-3.5 flex items-center gap-3 max-w-[17rem]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#D32F2F] pulse-dot shrink-0" />
              <div className="text-[13px] font-semibold text-[#0F172A] leading-snug">A guard is watching your community right now.</div>
            </div>
          </div>
        </div>
      </div>

      <VideoModal isOpen={showVideo} onClose={() => setShowVideo(false)} videoUrl="https://youtu.be/_Qjvu0u8BcM" title="Redline Monitoring" />
    </section>
  );
};

export default Hero;
